import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { EmployerService } from './employer.service';
import { ErrorHandlerService } from './error-handler.service';
import { AdapterTypeEnum } from '../enums/adapter-type-enum';
import { LicenseMachineEnum } from '../enums/license-machine-enum';
import { EmployerDto, MachineV2Dto, MachineRestService, UserGrantDto } from '../api/exa';
import { AuthService } from './auth.service';
import { NGXLogger } from 'ngx-logger';
import { ExaGrant } from '../model/exa-grant';
import { HttpClient } from '@angular/common/http';
import { AlertService } from './alert.service';
import { TranslateService } from '@ngx-translate/core';
import { MachineState } from '../pages/map/map.page';
import { LocationStatusItem } from '../model/location-status-item';
import { AdapterStatusItem } from '../model/adapter-status-item';
import { PubSubService } from './pubsub.service';
import { TrackPosition } from '../components/exa-new-map/exa-new-map.component';

@Injectable({
  providedIn: 'root',
})
export class MachineService {
  private employerDto: EmployerDto = null;

  private machines: MachineV2Dto[] = [];
  private machines$ = new BehaviorSubject<MachineV2Dto[]>([]);
  private machinesWD: MachineV2Dto[] = [];
  private machinesWD$ = new BehaviorSubject<MachineV2Dto[]>([]);

  // CONFIG LISTS
  private tractorList: BehaviorSubject<any> = null; // TRACTOR
  private combineList: BehaviorSubject<any> = null; // COMBINE
  private forageHarvesterList: BehaviorSubject<any> = null; // FORAGE_HARVESTER
  private telehandlerList: BehaviorSubject<any> = null;
  private wheelloaderList: BehaviorSubject<any> = null;
  private excavatorList: BehaviorSubject<any> = null;
  public onlinesMachinesWhenLeaving: MachineState[] = [];
  private unsubscribe$ = new Subject<void>();
  private authUser: UserGrantDto = null;
  

    // caches for selected employers
  private trackLengths: Map<string, number> = new Map();
  private machineStates: Map<string, Map<number, LocationStatusItem[]>> = new Map();
  private adapterStates: Map<string, Map<string, AdapterStatusItem>> = new Map();

  constructor(
    private httpClient: HttpClient,
    private access: AuthService,
    private employerService: EmployerService,
    private machineRest: MachineRestService,
    private errorHandler: ErrorHandlerService,
    private log: NGXLogger,
    private pubsub: PubSubService,
    private alertService: AlertService,
    private translate: TranslateService
  ) {
    this.employerService.onSelectEmployer().pipe(takeUntil(this.unsubscribe$)).subscribe((employer) => { 
      this.employerDto = employer; 
          this.authUser = this.access.authUserData;    
          if (employer?.username) {
          // add new Maps for this employer if not available
          const username = employer.username;
          if (!this.trackLengths.has(username)) {
            this.trackLengths.set(username, 240);
          }
          if (!this.machineStates.has(username)) {
            this.machineStates.set(username, new Map());
          }
          if (!this.adapterStates.has(username)) {
            this.adapterStates.set(username, new Map());
          }
        }
    });

     this.pubsub
      .onAdapterStatusUpdate()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((status) => {
        if (status) {
          const employer = status[0];
          const item: AdapterStatusItem = status[1];
          if (!this.adapterStates.has(employer)) {
            this.adapterStates.set(employer, new Map());
          }
          this.adapterStates.get(employer)!.set(item.an!, item);
        }
      });

       this.pubsub
      .onStatusUpdate()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((status) => {
        if (status) {
          const employer = status[0];
          const item: LocationStatusItem = status[1];
          if (!this.machineStates.has(employer)) {
            this.machineStates.set(employer, new Map());
          }
          if (!this.machineStates.get(employer)!.has(item.mId!)) {
            this.machineStates.get(employer)!.set(item.mId!, []);
          }
          // append to existing list
          this.machineStates.get(employer)!.get(item.mId!)!.push(item);
          // we also have to check the length limit set for this employer, at max store 240min
          // divide by 10 -> every 10 seconds there is a new pubsub position
          const maxTrackLength = ((this.trackLengths.get(employer) ?? 240) * 60) / 10;
          this.pruneLocationsIfNeeded(this.machineStates.get(employer)!.get(item.mId!)!, maxTrackLength);
        
        if(this.machineStates.get(employer)) this.setItemWithExpiry(this.authUser.username + '-' + employer + '-machineStates', this.machineStates.get(employer), 60 * 60 * 1000);
          const obj = JSON.parse(sessionStorage.getItem(this.authUser.username + '-' + employer + '-machineStates'));
        }
      });
  }

   private setItemWithExpiry(key, value, ttl) {
    const now = Date.now();
    const item = {
      value:  Array.from(value.entries()),
      user: this.authUser.username,
      employer: this.employerDto?.username,
      expiry: now + ttl, // Timestamp when the item expires
    };
    // console.log(item);
        
    sessionStorage.setItem(key, JSON.stringify(item));
  }

   getMachineStates(employer: string): Map<number, LocationStatusItem[]> | undefined {
    if (this.machineStates.has(employer)) {
      return this.machineStates.get(employer);
    }
    return undefined;
  }

  private pruneLocationsIfNeeded(locationItems: LocationStatusItem[], maxLength: number) {
    const elementsToRemove = locationItems.length - maxLength;
    // iterate through the paths and remove from the front
    if (elementsToRemove > 0) {
      locationItems.splice(0, elementsToRemove);
    }
  }

   updateTrack(employer: string, machineId: number, track: TrackPosition[]) {
    if (!this.machineStates.has(employer)) {
      this.machineStates.set(employer, new Map());
    }
    this.machineStates.get(employer)!.set(machineId, track);
    const maxTrackLength = ((this.trackLengths.get(employer) ?? 240) * 60) / 10;
    this.pruneLocationsIfNeeded(this.machineStates.get(employer)!.get(machineId)!, maxTrackLength);
      if(this.machineStates.get(employer)) this.setItemWithExpiry(this.authUser.username + '-' + employer + '-machineStates', this.machineStates.get(employer), 60 * 60 * 1000);
  }

  loadMachines() {
   this.loadMachineList();

    this.employerService.onSelectEmployer().subscribe((employer) => {
      this.employerDto = employer;

      if (this.employerDto) {
        this.reloadMachines();
      } else {
        this.machines = [];
        this.machinesWD = [];
        this.machines$.next(this.machines);
        this.machinesWD$.next(this.machinesWD);
      }
    });

    this.log.debug('Construct');
  }

  reloadMachines() {
    this.machinesWD = [];
    this.machines = [];
    if (this.employerDto && this.access.hasGrantOwner(ExaGrant.READ_MACHINE, this.employerDto.id)) {
      this.machineRest.getMachinesUsingPOST(this.employerDto.username, { size: 300 }).subscribe(
        (machines) => {
          this.machinesWD = machines.content;
          this.machines = this.filterDeletedElements(this.machinesWD);
          this.machines$.next(this.machines);
          this.machinesWD$.next(this.machinesWD);
        },
        (err) => {
          this.machines = [];
          this.machinesWD = [];
          this.machines$.next(this.machines);
          this.machinesWD$.next(this.machinesWD);
          this.errorHandler.handleResponseError(err);
        }
      );
      this.employerService.refreshActivatedLicenses();
    } else {
      this.machines$.next(this.machines);
      this.machinesWD$.next(this.machinesWD);
    }
  }

  private filterDeletedElements(elementsWithDeleted: MachineV2Dto[]): MachineV2Dto[] {
    return elementsWithDeleted.filter((elem) => !elem.deleted);
  }

  private filterNonDeletedElements(elementsWithDeleted: MachineV2Dto[]): MachineV2Dto[] {
    return elementsWithDeleted.filter((elem) => elem.deleted);
  }

  onGetMachines(withDeleted?: boolean): Observable<MachineV2Dto[]> {
    this.loadMachines();
    if (withDeleted) {
      return this.machinesWD$;
    }
    return this.machines$;
  }

  getMachinesInactive(): MachineV2Dto[] {
    return this.filterNonDeletedElements(this.machinesWD);
  }

  onGetMachinesSorted(withDeleted: boolean): Observable<MachineV2Dto[]> {
    return this.onGetMachines(withDeleted).pipe(
      map((arr) =>
        arr.sort((a, b) => {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        })
      )
    );
  }

  getMachineById(machineId: number): MachineV2Dto {
    if (machineId) {      
      for (const machine of this.machinesWD) {
        if (machine.id == machineId) {          
          return machine;
        }
      }
    }
    return null;
  }

  getMachinesWithT2(minimumLicense?: LicenseMachineEnum): MachineV2Dto[] {
    const machinesWAdapter = [];
    for (const machine of this.machines) {
      if (machine.pairedAdapter.type && machine.pairedAdapter.type === AdapterTypeEnum.T2) {
        if (minimumLicense) {
          if (this.hasAtLeastLicense(minimumLicense, machine)) {
            machinesWAdapter.push(machine);
          }
        } else {
          machinesWAdapter.push(machine);
        }
      }
    }
    return machinesWAdapter;
  }

  hasAtLeastLicense(license: LicenseMachineEnum, machine: MachineV2Dto): boolean {
    if (machine && machine.machineSettings && machine.machineSettings.activeLicense) {
      if (license == LicenseMachineEnum.ULTIMATE && machine.machineSettings.activeLicense == LicenseMachineEnum.ULTIMATE) {
        return true;
      }
      if (
        license == LicenseMachineEnum.PRO &&
        (machine.machineSettings.activeLicense == LicenseMachineEnum.ULTIMATE ||
          machine.machineSettings.activeLicense == LicenseMachineEnum.PRO)
      ) {
        return true;
      }
      if (
        license == LicenseMachineEnum.LIGHT &&
        (machine.machineSettings.activeLicense == LicenseMachineEnum.ULTIMATE ||
          machine.machineSettings.activeLicense == LicenseMachineEnum.PRO ||
          machine.machineSettings.activeLicense == LicenseMachineEnum.LIGHT)
      ) {
        return true;
      }
    }
    return false;
  }

  loadMachineList() {
    this.tractorList = new BehaviorSubject(null);
    this.combineList = new BehaviorSubject(null);
    this.forageHarvesterList = new BehaviorSubject(null);
    this.telehandlerList = new BehaviorSubject(null);
    this.wheelloaderList = new BehaviorSubject(null);
    this.excavatorList = new BehaviorSubject(null);

    this.httpClient.get('./assets/ressources/machine_list.json').subscribe((data) => {
      this.tractorList.next(data['TRACTOR']);
      this.combineList.next(data['COMBINE']);
      this.forageHarvesterList.next(data['FORAGE_HARVESTER']);
      this.telehandlerList.next(data['TELEHANDLER']);
      this.wheelloaderList.next(data['WHEELLOADER']);
      this.excavatorList.next(data['EXCAVATOR']);
    });
  }

  getTractorList(): Observable<any> {
    return this.tractorList;
  }

  getCombineList(): Observable<any> {
    return this.combineList;
  }

  getForageHarvesterList(): Observable<any> {
    return this.forageHarvesterList;
  }

  getTelehandlerList(): Observable<any> {
    return this.telehandlerList;
  }

  getWheelloaderList(): Observable<any> {
    return this.wheelloaderList;
  }

  getExcavatorList(): Observable<any> {
    return this.excavatorList;
  }

  getTractorMakes(): Observable<any> {
    return this.getTractorList().pipe(
      map((e) => {
        if (e) {
          return Object.keys(e).sort(this.sortString);
        }
      })
    );
  }

  getCombineMakes(): Observable<any> {
    return this.getCombineList().pipe(
      map((e) => {
        if (e) {
          return Object.keys(e).sort(this.sortString);
        }
      })
    );
  }

  getForageHarvesterMakes(): Observable<any> {
    return this.getForageHarvesterList().pipe(
      map((e) => {
        if (e) {
          return Object.keys(e).sort(this.sortString);
        }
      })
    );
  }

  getTelehandlerMakes(): Observable<any> {
    return this.getTelehandlerList().pipe(
      map((e) => {
        if (e) {
          return Object.keys(e).sort(this.sortString);
        }
      })
    );
  }

  getWheelloaderMakes(): Observable<any> {
    return this.getWheelloaderList().pipe(
      map((e) => {
        if (e) {
          return Object.keys(e).sort(this.sortString);
        }
      })
    );
  }

  getExcavatorMakes(): Observable<any> {
    return this.getExcavatorList().pipe(
      map((e) => {
        if (e) {
          return Object.keys(e).sort(this.sortString);
        }
      })
    );
  }

  getTractorModels(make: string): Observable<any> {
    return this.getTractorList().pipe(map((e) => (e[make] ? e[make].sort(this.sortString) : [])));
  }

  getCombineModels(make: string): Observable<any> {
    return this.getCombineList().pipe(map((e) => (e[make] ? e[make].sort(this.sortString) : [])));
  }

  getForageHarvesterModels(make: string): Observable<any> {
    return this.getForageHarvesterList().pipe(map((e) => (e[make] ? e[make].sort(this.sortString) : [])));
  }

  getTelehandlerModels(make: string): Observable<any> {
    return this.getTelehandlerList().pipe(map((e) => (e[make] ? e[make].sort(this.sortString) : [])));
  }

  getWheelloaderModels(make: string): Observable<any> {
    return this.getWheelloaderList().pipe(map((e) => (e[make] ? e[make].sort(this.sortString) : [])));
  }

  getExcavatorModels(make: string): Observable<any> {
    return this.getExcavatorList().pipe(map((e) => (e[make] ? e[make].sort(this.sortString) : [])));
  }

  private sortString(n1, n2) {
    if (n1 > n2) {
      return 1;
    }
    if (n1 < n2) {
      return -1;
    }
    return 0;
  }

  getFormattedNameById(machineId: number) {
    if (machineId) {
      const mach: MachineV2Dto = this.getMachineById(machineId);
      if (mach) {
        if (mach.name && mach.name.trim()) {
          return mach.name;
        } else {
          return mach.make + ' ' + mach.model;
        }
      }
    }
    return '';
  }

  getFormattedName(machine: MachineV2Dto) {
    if (machine) {
      if (machine.name && machine.name.trim()) {
        return machine.name;
      } else {
        return machine.make + ' ' + machine.model;
      }
    }
    return '';
  }

  hasLongOfflineMachine(): boolean {
    var dateOffset = 24 * 60 * 60 * 1000 * 4; //4 days
    let deadline = new Date();
    deadline.setTime(deadline.getTime() - dateOffset);
    for (const machine of this.machines) {
      if (machine.lastPosition) {
        if (machine.lastPosition.time && Number(machine.lastPosition.time) <= deadline.getTime()) {
          return true;
        }
      }
    }
    return false;
  }

  getLongOfflineMachineCount(): number {
    var dateOffset = 24 * 60 * 60 * 1000 * 4; //4 days
    let deadline = new Date();
    deadline.setTime(deadline.getTime() - dateOffset);
    let i = 0;
    for (const machine of this.machines) {
      if (machine.lastPosition.time && Number(machine.lastPosition.time) <= deadline.getTime()) {
        i++;
      }
    }
    return i;
  }

  isLongOffline(machine: MachineV2Dto) {
    var dateOffset = 24 * 60 * 60 * 1000 * 4; //4 days
    let deadline = new Date();
    deadline.setTime(deadline.getTime() - dateOffset);
    return machine.lastPosition.time && Number(machine.lastPosition.time) <= deadline.getTime();
  }

  addMachine(enterpriseUsername: string, machineInfo: MachineV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.machineRest.addMachineUsingPOST(enterpriseUsername, machineInfo).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('MACHINE.ADD.SUCCESS'));
          console.log(' Maschine hinzugefügt.');
          this.reloadMachines();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  editMachine(machineID: number, machineInfo: MachineV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.machineRest.updateMachineUsingPUT(machineID, machineInfo).subscribe(
        (ret) => {
          this.alertService.openMessageToast(this.translate.instant('MACHINE.EDIT.SUCCESS'));
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  deleteMachine(machineId): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.machineRest.deleteMachineUsingDELETE(machineId).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('MACHINE.DELETE.SUCCESS'));
          console.log(' machineRest.deleteMachineUsingDELETE -> SUCCESS');
          this.reloadMachines();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          console.log(' machineRest.deleteMachineUsingDELETE -> ERROR');
          reject(err);
        }
      );
    });
  }
}
